/**
 * Sitewide Force Ads OFF — a super-admin-only DB flag (ad_settings.global_ads_off) that turns every ad placement
 * off for EVERY visitor. The opposite of lib/global-force-ads.ts, and it wins over it (and over per-browser test
 * mode): isAdTestModeOn() returns false while this is on.
 *
 * Two layers, so there is no gap:
 * - Server: every ad-serving route checks the flag (lib/global-ads-off-server.ts) and returns no ad. This is what
 *   protects a first-time visitor whose browser has no cached copy yet.
 * - Client: this localStorage-mirrored synchronous read (same pattern and tradeoff as global-force-ads.ts) lets the
 *   ad components skip rendering and fetching right away.
 */

import { useEffect, useState } from "react"

const CACHE_KEY = "goonru_global_ads_off_cache"
const REFRESH_INTERVAL_MS = 30_000

interface CacheEntry {
  enabled: boolean
  fetchedAt: number
}

function readCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.enabled === "boolean" ? parsed : null
  } catch {
    return null
  }
}

function writeCache(enabled: boolean): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ enabled, fetchedAt: Date.now() } satisfies CacheEntry))
    window.dispatchEvent(new Event("global-ads-off-changed"))
  } catch {
    // best-effort
  }
}

let refreshInFlight: Promise<void> | null = null

function refreshInBackground(): void {
  if (refreshInFlight) return
  refreshInFlight = fetch("/api/admin/global-ads-off")
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (typeof d?.enabled === "boolean") writeCache(d.enabled)
    })
    .catch(() => {})
    .finally(() => {
      refreshInFlight = null
    })
}

/** Synchronous read of the sitewide ads-off flag. */
export function isGlobalAdsOff(): boolean {
  if (typeof window === "undefined") return false
  const cached = readCache()
  if (!cached || Date.now() - cached.fetchedAt > REFRESH_INTERVAL_MS) refreshInBackground()
  return cached?.enabled ?? false
}

/**
 * The cached flag WITHOUT assuming a default: true / false once this browser has seen the server's answer,
 * null if it has not yet (first visit). isGlobalAdsOff() collapses "unknown" into false, which is fine for
 * "should I hide an ad" but wrong for "should I hold space for one".
 */
export function readGlobalAdsOffCache(): boolean | null {
  if (typeof window === "undefined") return null
  return readCache()?.enabled ?? null
}

/** Admin-only. Throws on failure (non-admins are rejected server-side). */
export async function setGlobalAdsOff(enabled: boolean): Promise<void> {
  const res = await fetch("/api/admin/global-ads-off", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new Error(body?.error || `Failed to update Force Ads OFF (HTTP ${res.status})`)
  }
  writeCache(enabled)
}

export function useGlobalAdsOff(): boolean {
  const [value, setValue] = useState(false)
  useEffect(() => {
    const check = () => setValue(isGlobalAdsOff())
    check()
    window.addEventListener("global-ads-off-changed", check)
    window.addEventListener("storage", check)
    return () => {
      window.removeEventListener("global-ads-off-changed", check)
      window.removeEventListener("storage", check)
    }
  }, [])
  return value
}
